import React, { useMemo } from 'react';
import * as T from 'prop-types';
import { Form } from 'react-final-form';
import createDecorator from 'final-form-focus';
import Question from './Question';
import Disclaimer from '../../../commons/Disclaimer';
import { useAutoSaveDraft } from '../../../commons/draftHooks';
import { PageErrorComponent } from '../../../commons/PageErrorComponent';

const OversightQuestionnairePage = (props) => {
    const { oqs, projectCompanies } = props;

    return (
        <div>
            <Disclaimer>
                <p>
                    {`Please answer the following Oversight Questions with respect to consultations regarding ${projectCompanies.join(
                        ', '
                    )}`}
                </p>
            </Disclaimer>
            <div className="max-w-4xl mx-auto bg-white px-6 py-4 md:mt-6 md:mb-6 rounded shadow-md text-sm">
                <ol className={'pl-4'}>
                    {oqs.map((question, index) => (
                        <li type="1" key={index}>
                            <Question
                                question={question}
                                key={index}
                                prefix={'application.oqs.q'}
                                suffix={'answer'}
                                projectCompanies={projectCompanies}
                            />
                        </li>
                    ))}
                </ol>
                {/* uncomment below line to see the oqs received from backend. */}
                {/* <pre>{JSON.stringify(oqs, undefined, 2)}</pre> */}
            </div>
        </div>
    );
};

OversightQuestionnairePage.propTypes = {
    currentPage: T.number,
    oqs: T.arrayOf(T.object),
    projectCompanies: T.arrayOf(T.string),
};

const Page4Form = (props) => {
    // return same decorator for subsequent renders.
    const focusOnError = useMemo(() => createDecorator(), []);
    const { disableSubmit, nextFn, prevFn } = props;
    return (
        <Form
            initialValues={props.initialValues}
            decorators={[focusOnError]}
            keepDirtyOnReinitialize
            onSubmit={(values) => {
                nextFn(values);
            }}
            render={({
                values,
                errors,
                handleSubmit,
                submitFailed,
                submitErrors,
            }) => {
                useAutoSaveDraft(props.autoSaveCallback, values, errors);

                return (
                    <form onSubmit={handleSubmit}>
                        <OversightQuestionnairePage
                            oqs={props.oqs}
                            projectCompanies={props.projectCompanies}
                        />
                        {!submitErrors && submitFailed && errors && (
                            <PageErrorComponent error={errors} />
                        )}
                        <button
                            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                            type="button" // normal button because people should be able to move backward without getting stuck on this page due to validation.
                            onClick={() => prevFn(values, errors)}
                        >
                            &#60; Previous Page
                        </button>
                        <button
                            className="disabled:opacity-50 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded float-right"
                            type="submit"
                            disabled={disableSubmit}
                            data-testid="submit-form"
                        >
                            Submit &#62;
                        </button>
                    </form>
                );
            }}
        />
    );
};

Page4Form.propTypes = {
    initialValues: T.object,
    autoSaveCallback: T.func,
    nextFn: T.func,
    prevFn: T.func,
    oqs: T.arrayOf(T.object),
    projectCompanies: T.arrayOf(T.string),
    disableSubmit: T.bool,
};

export default Page4Form;
